import React from "react";
import {
    Database, Sparkles, Images, Plus, Share2, Copy, ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/**
 * iter-119ae — DashboardHeaderActions polished per user feedback ("The
 * dashboard UI is looking very clumsy and messy"). The original action
 * row exposed 6+ verbose outlined buttons in a single line (Back, View
 * sources, Add from Gallery, Add source, Share, Refresh) which together
 * made the dashboard header feel like a tool palette.
 *
 * Redesign:
 *   • "Back" stays as a leading icon-only button (universal affordance).
 *   • "View sources / Add from Gallery / Add source / Regenerate" now
 *     live inside a single "Sources & widgets ▾" dropdown — these
 *     actions are conceptually one cluster ("things that change what
 *     the dashboard renders") and most users only use them rarely.
 *   • "Share" and the per-dashboard auto-refresh schedule control stay
 *     visible because they're frequent. "Use template" stays as a
 *     primary CTA on template previews.
 *
 * No props changed — purely a presentation reshuffle. Test-IDs preserved
 * so existing automation still finds the buttons.
 */
export default function DashboardHeaderActions({
    dashboard,
    canEdit,
    canWriteData,
    onBack,
    onRebuildAuto,
    onViewSourceDataset,
    onViewSources,
    onRegenerateFromTemplate,
    onOpenGallery,
    onAddSource,
    onShare,
    onClone,
    scheduleControl,
    // iter-121.132 — Optional slot rendered IMMEDIATELY after the
    // leading ArrowLeft back button. Currently used by the
    // SA-only "Apply Theme" pill so it sits beside the back arrow
    // (was previously hanging at the left edge of the header).
    afterBackSlot,
}) {
    const src = dashboard?.source || {};
    const isAuto = !!src.auto;
    const isTemplate = !!dashboard?.is_template;
    const isTemplateBound = !!(
        dashboard?.is_unified
        || dashboard?.template_slug
        || (dashboard?.bind_to_models || []).length > 0
    );

    // Build the dropdown contents based on dashboard type + role; if
    // empty we don't render the dropdown at all.
    const dropdownItems = [];
    if (isAuto && src.dataset_id) {
        dropdownItems.push({
            key: "view-source-ds", icon: Database, label: "View source dataset",
            onClick: onViewSourceDataset, testid: "dashboard-view-source",
        });
        if (canEdit) {
            dropdownItems.push({
                key: "rebuild-auto", icon: Sparkles, label: "Regenerate from source",
                onClick: onRebuildAuto, testid: "dashboard-rebuild-auto",
            });
        }
    }
    if (isTemplateBound) {
        dropdownItems.push({
            key: "view-sources", icon: Database, label: "View sources",
            onClick: onViewSources, testid: "dashboard-view-sources",
        });
        if (canEdit && dashboard?.template_slug) {
            dropdownItems.push({
                key: "regen-tpl", icon: Sparkles, label: "Regenerate from template",
                onClick: onRegenerateFromTemplate, testid: "dashboard-regenerate-template",
            });
        }
    }
    if (canEdit) {
        dropdownItems.push({
            key: "add-gallery", icon: Images, label: "Add widget from Gallery",
            onClick: onOpenGallery, testid: "dashboard-add-from-gallery",
        });
    }
    if (canEdit && canWriteData && !isTemplate) {
        dropdownItems.push({
            key: "add-source", icon: Plus, label: "Add source connector",
            onClick: onAddSource, testid: "dashboard-add-source",
        });
    }

    return (
        <>
            {afterBackSlot}

            {dropdownItems.length > 0 && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="rounded-sm h-9" data-testid="dashboard-sources-menu">
                            <Database className="h-4 w-4 mr-1.5" /> Sources & widgets
                            <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        {dropdownItems.map((it, i) => (
                            <React.Fragment key={it.key}>
                                {/* Visually separate "view" items from "add" items */}
                                {i > 0 && it.key.startsWith("add-") && !dropdownItems[i - 1].key.startsWith("add-") && (
                                    <DropdownMenuSeparator />
                                )}
                                <DropdownMenuItem onClick={it.onClick} data-testid={it.testid} className="cursor-pointer">
                                    <it.icon className="h-4 w-4 mr-2 opacity-70" />
                                    {it.label}
                                </DropdownMenuItem>
                            </React.Fragment>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}

            {/* iter-110.11: hard-share stays as a top-level button (frequent action). */}
            {canEdit && !isTemplate && (
                <Button variant="outline" size="sm" className="rounded-sm h-9"
                    onClick={onShare}
                    title="Share with people in your scope or invite by email"
                    data-testid="dashboard-share">
                    <Share2 className="h-4 w-4 mr-1.5" /> Share
                </Button>
            )}

            {isTemplate && (
                <Button size="sm" className="rounded-sm h-9" onClick={onClone} data-testid="dashboard-clone">
                    <Copy className="h-4 w-4 mr-1.5" /> Use template
                </Button>
            )}

            {/* Auto-refresh selector last so it sits at the far-right. */}
            {!isTemplate && scheduleControl}
        </>
    );
}
